// Form-kind registry for the offline submission queue. Each entry maps a
// stable form_kind string to its destination Supabase table + a
// buildRecord(payload, ids) function that assembles the row to insert.
//
// Five flat kinds: fuel supply and poultry/pig/cattle/sheep daily reports.
// Add Feed and the other parent RPC forms use offlineRpcForms instead.
//
// Flat queue idempotency: useOfflineSubmit sends this frozen record through
// submit_queued_report (229). Its private receipt binds the authenticated actor,
// stable row/CSID and canonical original request. Unique violations do not
// acknowledge queued work. Legacy and conflicting work requires explicit Open
// recovery; newly queued work records its authenticated actor before writing.
// Other RPC/multirow queues retain their own contract and are outside 229.
//
// Parent-scoped submissions remain separate: offlineRpcForms and
// useOfflineRpcSubmit retain one atomic Add Feed parent + children request.
// Never fan that request into this per-row registry; that loses atomicity and
// photo attribution. Migration 229 does not certify those existing RPC replays.
//
// Photo metadata includes name/path/mime/size/captured_at plus a SHA-256 digest
// of the frozen compressed bytes for this receipt boundary.
//
// Don't rely on a fall-through "default form_kind" — getFormConfig throws
// on unknown kinds so a typo in a hook call is loud, not silent.

// The two identity fields the hook mints once per submission and reuses on
// every retry. Both are required: a record built without them cannot dedupe.
export type SubmissionIds = {
  /** client-generated row id, stable across retries */
  id: string;
  /** client_submission_id, stable across retries */
  csid: string;
};

// A form payload as it arrives from the form layer.
//
// The VALUES are deliberately `unknown` rather than a per-form schema. This
// registry never inspects them beyond the Array.isArray / != null / !! checks
// written below — every other field is copied verbatim into the row, and the
// form layer (not this module) owns coercion, per the shape rules above. A
// hand-written per-field schema here would be a claim about caller data that
// nothing in this repository verifies, and the first form that legitimately
// passes a string where the schema said number would be "fixed" by loosening
// the schema rather than by fixing the form.
export type FormPayload = Readonly<Record<string, unknown>>;

// The frozen row handed to submit_queued_report as p_record. Its
// KEYS are the knowable part and each buildRecord below enumerates them; the
// values are pass-through payload data, so they stay `unknown`.
export type FormRecord = Record<string, unknown>;

export type FormConfig = {
  /** destination table owned by the server receipt boundary */
  readonly table: string;
  readonly hasPhotos: boolean;
  /**
   * Existing domain create owner invoked by the server receipt boundary.
   * Other online clients may also call this owner directly.
   */
  readonly submitRpc?: string;
  readonly buildRecord: (payload: FormPayload, ids: SubmissionIds) => FormRecord;
};

// Annotated rather than inferred so every entry is checked against the config
// contract at its definition site. `Record<string, FormConfig>` (not a union of
// the five literal keys) is what lets getFormConfig take the runtime string a
// hook was constructed with and answer for it without a cast — the unknown-kind
// throw below is the real gate, and it stays the only one.
const REGISTRY: Readonly<Record<string, FormConfig>> = Object.freeze({
  fuel_supply: Object.freeze({
    table: 'fuel_supplies',
    hasPhotos: false,
    /**
     * payload — form-collected fields
     *   {date, gallons, fuel_type, destination, team_member, notes}
     */
    buildRecord(payload: FormPayload, {id, csid}: SubmissionIds): FormRecord {
      return {
        id,
        client_submission_id: csid,
        date: payload.date,
        gallons: payload.gallons,
        fuel_type: payload.fuel_type ?? null,
        destination: payload.destination,
        team_member: payload.team_member,
        notes: payload.notes ?? null,
        source: 'webform',
      };
    },
  }),

  // Phase 1C-B: PigDailys no-photo offline queue.
  // Phase 1D-A: hasPhotos flipped to true (semantically "supported", not
  // "required"). The hook short-circuits at submit() if payload.photos is
  // empty/absent, falling through to the existing flat path. Existing
  // tests/pig_dailys_offline.spec.js (4 active cases) lock that behavior.
  // (Phase 1C-B's 5th case — the photo-online-only "needs a connection"
  // assertion — was retired in 1D-A when photos became queue-capable.)
  //
  // Important shape rules:
  //   - NO `source` field — current PigDailys rows omit it; preserve the
  //     existing row shape exactly. The §7 contract for the
  //     `*_dailys.source` column treats null as the default, and existing
  //     dashboards filter by `source === 'add_feed_webform'` (not by
  //     'webform'). Adding a value here would change row semantics.
  //   - NO `feed_type` — pig_dailys has no such column (§7).
  //   - `photos` jsonb — paths-only metadata when payload.photos has entries
  //     (per the Phase 1D-A prepared-photo flow). Empty array `[]` for the
  //     no-photo path (preserves the byte-identical row shape Phase 1C-B
  //     locked). Raw File/Blob refs NEVER reach the row — sanitized in the
  //     hook before buildRecord runs.
  //   - Numeric coercion (parseInt/parseFloat) happens at the form layer,
  //     not here. The registry passes payload values through unchanged so
  //     replay produces byte-identical rows.
  // Phase 1D-B: WebformHub broiler / cattle / sheep daily-report photo offline
  // queue. pig_dailys (above-flipped in 1D-A) is reused by both
  // PigDailysWebform standalone (1D-A) and WebformHub /webforms/pig (1D-B).
  // Layer (layer_dailys) deferred to 1D-C — setHousingAnchorFromReport is a
  // load-bearing post-sync DB write and needs an explicit replay-hook design.
  // Egg (egg_dailys) permanently excluded — mig 030 omits egg_dailys.photos.
  //
  // Each new entry's buildRecord mirrors the row shape today's WebformHub
  // submit handlers assemble at the time of direct-insert, MINUS raw Blob
  // refs (sanitized payload contract: payload.photos arrives as metadata
  // only here; the hook strips raw File[] before calling buildRecord).
  // Locked exact shapes:
  //   - poultry_dailys: NO source field (current rows omit it).
  //   - cattle_dailys: source: 'daily_webform' (current row literal).
  //   - sheep_dailys:  source: 'daily_webform'; mortality_count default 0.
  //   - All: photos: [] when payload.photos absent (consistent with pig_dailys).
  //
  // Critical replay invariant: cattle/sheep feeds + minerals jsonb arrays
  // are PASS-THROUGH from payload — the form does the cattle_feed_inputs
  // nutrition-snapshot lookup ONCE before calling submit(). Replay produces
  // byte-identical rows even if cattle_feed_inputs values change between
  // submit and replay (matches the §2.2 cattle Decision 4 snapshot contract:
  // "editing the parent feed in admin does NOT rewrite historical reports").
  poultry_dailys: Object.freeze({
    table: 'poultry_dailys',
    hasPhotos: true,
    /**
     * payload
     *   {date, team_member, batch_label,
     *    feed_type, feed_lbs, grit_lbs,
     *    group_moved, waterer_checked,
     *    mortality_count, mortality_reason, comments,
     *    photos?}
     */
    buildRecord(payload: FormPayload, {id, csid}: SubmissionIds): FormRecord {
      return {
        id,
        client_submission_id: csid,
        submitted_at: new Date().toISOString(),
        date: payload.date,
        team_member: payload.team_member,
        batch_label: payload.batch_label,
        feed_type: payload.feed_type,
        feed_lbs: payload.feed_lbs,
        grit_lbs: payload.grit_lbs,
        group_moved: payload.group_moved,
        waterer_checked: payload.waterer_checked,
        mortality_count: payload.mortality_count,
        mortality_reason: payload.mortality_reason,
        comments: payload.comments,
        photos: Array.isArray(payload.photos) ? payload.photos : [],
      };
    },
  }),

  cattle_dailys: Object.freeze({
    table: 'cattle_dailys',
    hasPhotos: true,
    // Existing create/audit owner. Migration 229 delegates here after its
    // receipt/legacy-collision checks; other clients still call it directly.
    submitRpc: 'submit_cattle_daily_report',
    /**
     * payload
     *   {date, team_member, herd,
     *    feedsJ, mineralsJ,        // pre-built jsonb arrays from form layer
     *    fence_voltage, water_checked,
     *    mortality_count?, mortality_reason?, issues,
     *    photos?}
     */
    buildRecord(payload: FormPayload, {id, csid}: SubmissionIds): FormRecord {
      return {
        id,
        client_submission_id: csid,
        submitted_at: new Date().toISOString(),
        date: payload.date,
        team_member: payload.team_member,
        herd: payload.herd,
        feeds: Array.isArray(payload.feedsJ) ? payload.feedsJ : [],
        minerals: Array.isArray(payload.mineralsJ) ? payload.mineralsJ : [],
        fence_voltage: payload.fence_voltage,
        water_checked: payload.water_checked,
        mortality_count: payload.mortality_count != null ? payload.mortality_count : 0,
        mortality_reason: payload.mortality_reason ?? null,
        issues: payload.issues ?? null,
        source: 'daily_webform',
        photos: Array.isArray(payload.photos) ? payload.photos : [],
      };
    },
  }),

  sheep_dailys: Object.freeze({
    table: 'sheep_dailys',
    hasPhotos: true,
    // Existing create/audit owner, delegated to by migration 229 as above.
    submitRpc: 'submit_sheep_daily_report',
    /**
     * payload
     *   {date, team_member, flock,
     *    feedsJ, mineralsJ,        // pre-built jsonb arrays
     *    fence_voltage_kv, waterers_working,
     *    mortality_count?,         // defaults to 0
     *    comments,
     *    photos?}
     */
    buildRecord(payload: FormPayload, {id, csid}: SubmissionIds): FormRecord {
      return {
        id,
        client_submission_id: csid,
        submitted_at: new Date().toISOString(),
        date: payload.date,
        team_member: payload.team_member,
        flock: payload.flock,
        feeds: Array.isArray(payload.feedsJ) ? payload.feedsJ : [],
        minerals: Array.isArray(payload.mineralsJ) ? payload.mineralsJ : [],
        fence_voltage_kv: payload.fence_voltage_kv,
        waterers_working: !!payload.waterers_working,
        mortality_count: payload.mortality_count != null ? payload.mortality_count : 0,
        comments: payload.comments ?? null,
        source: 'daily_webform',
        photos: Array.isArray(payload.photos) ? payload.photos : [],
      };
    },
  }),

  pig_dailys: Object.freeze({
    table: 'pig_dailys',
    hasPhotos: true,
    /**
     * payload — form-collected fields. The form coerces numerics before
     *   calling submit(); this registry just passes through. Expected keys:
     *     {date, team_member, batch_id, batch_label,
     *      pig_count, feed_lbs,
     *      group_moved, nipple_drinker_moved, nipple_drinker_working,
     *      troughs_moved, fence_walked, fence_voltage,
     *      issues,
     *      photos?}  // photo-meta array (path/name/mime/size_bytes/captured_at)
     *                // OR empty/absent for the no-photo flat path. Raw File/Blob
     *                // never reaches here — hook strips before calling.
     */
    buildRecord(payload: FormPayload, {id, csid}: SubmissionIds): FormRecord {
      return {
        id,
        client_submission_id: csid,
        submitted_at: new Date().toISOString(),
        date: payload.date,
        team_member: payload.team_member,
        batch_id: payload.batch_id,
        batch_label: payload.batch_label,
        pig_count: payload.pig_count,
        feed_lbs: payload.feed_lbs,
        group_moved: payload.group_moved,
        nipple_drinker_moved: payload.nipple_drinker_moved,
        nipple_drinker_working: payload.nipple_drinker_working,
        troughs_moved: payload.troughs_moved,
        fence_walked: payload.fence_walked,
        fence_voltage: payload.fence_voltage,
        issues: payload.issues,
        photos: Array.isArray(payload.photos) ? payload.photos : [],
      };
    },
  }),
});

export const FORM_KINDS: readonly string[] = Object.freeze(Object.keys(REGISTRY));

export function getFormConfig(formKind: string): FormConfig {
  const entry = REGISTRY[formKind];
  if (!entry) {
    throw new Error(`offlineForms: unknown form_kind ${JSON.stringify(formKind)}`);
  }
  return entry;
}

export function buildRecord(formKind: string, payload: FormPayload, ids: SubmissionIds): FormRecord {
  return getFormConfig(formKind).buildRecord(payload, ids);
}

// Only exported for tests; production callers go through getFormConfig.
export const _REGISTRY = REGISTRY;
